import { ETAPAS_PANEL, type EtapaPanel } from "@copi/shared";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils/cn";

interface StepperEtapasProps {
  /** Número de la etapa actual (1..12). Null cuando estado es terminal especial. */
  etapaActual: EtapaPanel | null;
}

/**
 * Stepper vertical de las 12 etapas del proceso de tesis (ADR 016).
 *
 * Vive como columna interna del panel — NO es el sidebar global del app
 * (ese lo provee `AppShell`). Solo lectura; no navega, no muta.
 *
 * Tres estados visuales por paso:
 *  - `done`: número < etapaActual
 *  - `active`: número === etapaActual
 *  - `todo`: número > etapaActual
 *
 * Si `etapaActual === null` (terminal especial), todos los pasos se
 * renderizan atenuados y sin "active" — la página debería mostrar un
 * `BannerTerminal` en lugar del contenido principal.
 */
export function StepperEtapas({ etapaActual }: StepperEtapasProps) {
  return (
    <nav aria-label="Etapas del proceso de tesis">
      <p className="mb-3 px-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Proceso de tesis
      </p>
      <ol className="relative">
        {/* Línea vertical de conexión entre los iconos. */}
        <div className="absolute left-[22px] top-3 bottom-3 w-px bg-border" aria-hidden="true" />
        {ETAPAS_PANEL.map((etapa) => {
          const estado: "done" | "active" | "todo" =
            etapaActual === null
              ? "todo"
              : etapa.numero < etapaActual
                ? "done"
                : etapa.numero === etapaActual
                  ? "active"
                  : "todo";
          return (
            <li key={etapa.numero} className="relative">
              <div
                aria-current={estado === "active" ? "step" : undefined}
                aria-label={`Etapa ${etapa.numero} de 12: ${etapa.titulo} (${estado === "done" ? "completada" : estado === "active" ? "en curso" : "pendiente"})`}
                className={cn(
                  "flex items-center gap-3 rounded-xl px-2 py-2.5",
                  estado === "active" && "bg-brand-50",
                )}
              >
                <span className="relative z-10 shrink-0">
                  {estado === "done" ? (
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-primary text-primary-foreground ring-4 ring-background">
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  ) : estado === "active" ? (
                    <span className="grid h-6 w-6 place-items-center rounded-full border-2 border-primary bg-card ring-4 ring-background">
                      <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                    </span>
                  ) : (
                    <span className="grid h-6 w-6 place-items-center rounded-full border border-border bg-card text-[11px] font-bold text-muted-foreground">
                      {etapa.numero}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    "text-[13.5px] leading-tight",
                    estado === "active" && "font-semibold text-foreground",
                    estado === "done" && "text-muted-foreground",
                    estado === "todo" && "text-foreground/85",
                    etapaActual === null && "text-muted-foreground/60",
                  )}
                >
                  {etapa.titulo}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
